import { forwardRef, useId, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Info, TriangleAlert } from 'lucide-react';
import { Tooltip } from './tooltip';
import { fieldDescription } from './field-description';

type Variant = 'primary' | 'secondary' | 'ghost' | 'soft' | 'danger';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant; size?: 'sm' | 'md' | 'lg'; block?: boolean; loading?: boolean; icon?: ReactNode;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', block, loading, icon, children, className = '', disabled, type = 'button', ...rest }, ref) {
  const cls = ['dz-btn', variant !== 'secondary' && `dz-btn--${variant}`, size !== 'md' && `dz-btn--${size}`, block && 'dz-btn--block', className].filter(Boolean).join(' ');
  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="dz-spinner" aria-hidden /> : icon}
      {children}
    </button>
  );
});

/** An icon-only button. Its name shows as our tooltip (never the browser's `title=`), with the shortcut if any. */
export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: ReactNode; shortcut?: string; tooltip?: ReactNode | false }>(
  function IconButton({ label, icon, className = '', type = 'button', shortcut, tooltip, title: _title, ...rest }, ref) {
    const btn = <button ref={ref} type={type} className={`dz-icon-btn ${className}`} aria-label={label} aria-keyshortcuts={shortcut} {...rest}>{icon}</button>;
    return tooltip === false ? btn : <Tooltip content={tooltip ?? label} shortcut={shortcut}>{btn}</Tooltip>;
  });

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <span className="dz-spinner" role="status" aria-label={label} />;
}

interface FieldShellProps { label: ReactNode; hint?: ReactNode; error?: ReactNode; id: string; children: ReactNode; labelHidden?: boolean; labelAs?: 'label' | 'span' }
/** Label, control, then hint or error. The label's id is `${id}-label` (for aria-labelledby). */
export function FieldShell({ label, hint, error, id, children, labelHidden, labelAs = 'label' }: FieldShellProps) {
  const cls = labelHidden ? 'dz-visually-hidden' : 'dz-field__label';
  const said = fieldDescription(id, hint, error);
  return (
    <div className="dz-field">
      {labelAs === 'span' ? <span id={`${id}-label`} className={cls}>{label}</span> : <label id={`${id}-label`} htmlFor={id} className={cls}>{label}</label>}
      {children}
      {said?.kind === 'error' ? <p className="dz-field__error" id={said.id} role="alert"><AlertCircle size={14} aria-hidden />{error}</p>
        : said ? <p className="dz-field__hint" id={said.id}>{hint}</p> : null}
    </div>
  );
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> { label: ReactNode; hint?: ReactNode; error?: ReactNode; labelHidden?: boolean; suffix?: ReactNode; prefix?: ReactNode }
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, labelHidden, suffix, prefix, id, className = '', ...rest }, ref) {
  const auto = useId(); const fid = id ?? auto;
  const describedBy = error ? `${fid}-error` : hint ? `${fid}-hint` : undefined;
  const input = <input ref={ref} id={fid} className={`dz-input ${className}`} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest} />;
  return (
    <FieldShell label={label} hint={hint} error={error} id={fid} labelHidden={labelHidden}>
      {suffix || prefix ? <div className="dz-input-group">{prefix && <span className="dz-input-group__addon">{prefix}</span>}{input}{suffix && <span className="dz-input-group__addon dz-input-group__addon--end">{suffix}</span>}</div> : input}
    </FieldShell>
  );
});

export const PasswordField = forwardRef<HTMLInputElement, Omit<TextFieldProps, 'type' | 'suffix'>>(function PasswordField(props, ref) {
  const [shown, setShown] = useState(false);
  return <TextField ref={ref} {...props} type={shown ? 'text' : 'password'} suffix={
    <button type="button" className="dz-icon-btn" style={{ width: 32, height: 32 }} onClick={() => setShown((s) => !s)} aria-label={shown ? 'Hide password' : 'Show password'} aria-pressed={shown}>
      {shown ? <EyeOff /> : <Eye />}
    </button>} />;
});

export function TextArea({ label, hint, error, id, className = '', ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: ReactNode; hint?: ReactNode; error?: ReactNode }) {
  const auto = useId(); const fid = id ?? auto;
  const describedBy = error ? `${fid}-error` : hint ? `${fid}-hint` : undefined;
  return <FieldShell label={label} hint={hint} error={error} id={fid}><textarea id={fid} className={`dz-textarea ${className}`} aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest} /></FieldShell>;
}

const NOTICE_ICONS = { info: Info, danger: AlertCircle, success: CheckCircle2, warning: TriangleAlert, neutral: Info };
export function Notice({ tone = 'neutral', children, role }: { tone?: keyof typeof NOTICE_ICONS; children: ReactNode; role?: 'alert' | 'status' }) {
  const Icon = NOTICE_ICONS[tone];
  return <div className={`dz-notice dz-notice--${tone}`} role={role}><Icon aria-hidden /><div>{children}</div></div>;
}

/** Initials in a circle. `accent` colours it by that account's own accent (account switcher), not the page's. */
export function Avatar({ name, size = 36, accent }: { name: string; size?: number; accent?: string }) {
  const initials = name.trim().split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('') || '?';
  return <span className="dz-avatar" data-accent={accent} style={{ width: size, height: size, fontSize: Math.max(12, Math.round(size * 0.38)) }} aria-hidden>{initials}</span>;
}

export function Kbd({ children }: { children: ReactNode }) { return <kbd className="dz-kbd">{children}</kbd>; }
